// 적 rally 등록 폼 — **관측을 넣는 자리이지 설계를 하는 자리가 아니다**
//
// 공격 폼은 CT 가 편성을 **짠다.** 여기는 정반대다 — 적이 이미 움직이고 있고 CT 는 인게임
// 카운트다운을 **보고 그 숫자만** 옮겨 적는다. 그래서 이 폼에는 타입도 offset 도 대기 시간
// 옵션도 없고, 넣는 것이 셋뿐이다.
//
//   적 roster · 지금 어느 단계인가 · 그 순간의 남은 시간
//
// **가운데 것이 2026-08-10 개정의 전부다.** 같은 `0:52` 가 rallying 중이면 아직 안 떴다는
// 뜻이고 행군 중이면 이미 날아오는 중이라, 단계를 안 받으면 착탄이 **적 marching 하나만큼**
// 통째로 어긋난다. 그 어긋남은 화면에 그럴듯한 시각으로 찍힌다.
//
// ── 시각 칸이 없는 것이 계약이다 ────────────────────────────────────────────
//
// 앵커는 **서버가** 찍는다. 계약 본문에 시각 칸이 아예 없는 것이 이미 그것을 강제하고,
// 클라이언트 시각을 앵커로 쓰면 실데이터 규칙 위반이다 — 여섯 나라에서 접속하는 이
// 제품에서는 그 오차가 그대로 착탄 오차가 된다. CT 가 시계를 읽어 적을 필요가 없다는 것이
// 이 화면의 핵심 편의이기도 하다.
//
// ── 새 적을 여기서 만들 수 없다 ─────────────────────────────────────────────
//
// select 에는 명부에 이미 있는 적만 온다. 만드는 자리가 둘이면 같은 사람이 두 줄로 생기고,
// 삭제 라우트가 지우는 행과 이 화면이 읽는 행이 갈린다 — CT 는 지웠다고 믿는데 여기에는
// 그 이름이 남는다. 서버도 같은 계약을 져서 모르는 id 에 404 를 낸다.
//
// ── mm:ss 위젯을 읽기만 한다 ────────────────────────────────────────────────
//
// `src/web/ui/time-input.tsx` 가 공용이고 **한 글자도 고치지 않는다.** 그 파일이 `ui/` 에
// 사는 이유가 정확히 이것이다 — attack 디렉터리에 있었으면 수비가 공격을 import 하게 되어
// 두 갈래가 서로를 붙잡는다. 새 위젯을 만들면 같은 `1:42.0` 이 어디서 읽혔느냐에 따라 다른
// 수가 되고, **그 차이는 화면에 보이지 않는다.**

import { useState } from 'react'

import type { Roster } from '../../../../contract/schema.ts'
// 계약의 `EnemyPhase` 는 TypeBox 값이라 타입 자리에 쓸 수 없다. 단계의 타입은 코어의 것이다.
import type { EnemyPhase } from '../../../../core/timing/defense.ts'
import {
  common as commonStrings,
  type CommonStrings,
  defense as defenseStrings,
  type DefenseStrings,
} from '../../../../i18n/index.ts'
import type { EnemyRallyDraft } from '../../../defense/use-defense.ts'
import { useLang } from '../../../shell/use-lang.ts'
import { parseTimeText, TimeInput, timeParseMessage } from '../../../ui/time-input.tsx'
import { Icon, IconTile } from '../../../ui/icons.tsx'

/** 지금 고른 언어의 문자열 한 벌. **모듈에 굳히지 않는다** — 굳히면 언어를 바꿔도 안 따라온다. */
function useStrings(): DefenseStrings {
  return defenseStrings(useLang().lang)
}

/** 시간 입력 안내와 거절은 여러 화면이 같은 글자를 내야 해서 공용 네임스페이스가 진다. */
function useCommon(): CommonStrings {
  return commonStrings(useLang().lang)
}

export interface RallyFormDefenseProps {
  /** 명부의 적군만. 아군을 섞으면 CT 가 자기 편에게 rally 를 붙일 수 있게 된다. */
  enemies: readonly Roster[]
  onAdd: (draft: EnemyRallyDraft) => Promise<string | null>
}

const PHASES: readonly EnemyPhase[] = ['rallying', 'marching']

export function RallyFormDefense(props: RallyFormDefenseProps): React.ReactElement {
  const t = useStrings()
  const c = useCommon()
  const [enemyId, setEnemyId] = useState('')
  const [phase, setPhase] = useState<EnemyPhase>('rallying')
  const [remaining, setRemaining] = useState('')
  const [message, setMessage] = useState('')
  const [timeMessage, setTimeMessage] = useState('')
  const [done, setDone] = useState(false)

  const submit = async (): Promise<void> => {
    setDone(false)
    if (enemyId === '') {
      setMessage(t.formNeedEnemy)
      return
    }
    const parsed = parseTimeText(remaining)
    if (!parsed.ok) {
      setTimeMessage(timeParseMessage(parsed.reason, c))
      return
    }
    setMessage('')
    setTimeMessage('')

    // **사람이 친 글자를 지우지 않는다.** 성공하면 칸을 비우는데 그 비움은 왕복 **뒤에**
    // 온다. CT 가 응답을 기다리는 동안 다음 적의 숫자를 치기 시작하면 뒤늦게 도착한 앞
    // 응답이 방금 친 글자를 함께 지우고, 화면은 그 사실을 말해 주지 않는다 — 명부 등록
    // 폼에서 2026-08-15 에 실제로 관측된 결함이다. 실전에서 적 여럿을 연달아 올리는
    // 상황이 정확히 그 모양이라, 여기서는 **보낸 그 글자가 아직 그대로일 때만** 비운다.
    const submitted = remaining
    const error = await props.onAdd({
      enemyRosterId: enemyId,
      phase,
      remainingMs: parsed.millis,
    })
    if (error !== null) {
      setMessage(error)
      return
    }
    setDone(true)
    setRemaining((current) => (current === submitted ? '' : current))
  }

  return (
    <section data-testid="rally-form-defense" className="card min-w-0" aria-label={t.formTitle}>
      <div className="panel-head cat-cyan">
        <IconTile name="defense" tone="cyan" />
        <h3 className="section-title">{t.formTitle}</h3>
      </div>
      <p className="mt-line text-xs text-ink-mute">{t.formWhy}</p>

      <label className="mt-group flex min-w-0 flex-col gap-1 text-xs">
        <span className="font-medium text-ink-dim">{t.formEnemy}</span>
        <select
          data-testid="defense-enemy"
          className="field px-2.5 text-sm"
          value={enemyId}
          onChange={(event) => {
            setEnemyId(event.target.value)
            setMessage('')
          }}
        >
          {/* 빈 값이 「아직 안 골랐다」이고 그것이 곧 거절 사유다. 첫 적을 미리 골라 두면
              CT 가 고른 적 없는 이름으로 rally 가 올라간다. */}
          <option value="">{props.enemies.length === 0 ? t.formEnemyEmpty : t.formEnemyPick}</option>
          {props.enemies.map((row) => (
            <option key={row.id} value={row.id}>
              {row.nickname}
            </option>
          ))}
        </select>
      </label>
      {/* **길이 없다는 사실을 화면이 말한다.** 없는 문을 찾아 헤매는 것도, 있는 문을 못 찾는
          것도 같은 실패다 — 명부 스텝이 「길이 있는데 화면이 말하지 않으면 없는 것과
          같다」로 1점을 깎였다. */}
      <p data-testid="defense-enemy-where" className="mt-line text-xs text-ink-mute">
        {t.formEnemyOnlyRoster}
      </p>

      <fieldset className="mt-group min-w-0">
        <legend className="text-xs font-medium text-ink-dim">{t.formPhase}</legend>
        <div className="mt-group flex flex-wrap gap-3">
          {PHASES.map((candidate) => (
            <label key={candidate} className="flex items-center gap-1.5 py-control text-sm">
              <input
                type="radio"
                className="size-5"
                name="defense-phase"
                data-testid={`defense-phase-${candidate}`}
                checked={phase === candidate}
                onChange={() => {
                  setPhase(candidate)
                }}
              />
              <span>{candidate === 'rallying' ? t.formPhaseRallying : t.formPhaseMarching}</span>
            </label>
          ))}
        </div>
        <p className="mt-line text-xs text-ink-mute">{t.formPhaseWhy}</p>
      </fieldset>

      <div className="mt-group min-w-0">
        <TimeInput
          testId="defense-remaining"
          label={t.formRemaining}
          value={remaining}
          message={timeMessage}
          onChange={(next) => {
            setRemaining(next)
            // 한 글자만 고쳐도 앞의 거절을 거둔다. 남겨 두면 화면이 지금 값이 아닌 것을
            // 근거로 거절을 말하게 되고, 그 문장은 사람이 고친 뒤에도 계속 틀렸다고 한다.
            setTimeMessage('')
            setDone(false)
          }}
        />
      </div>

      <button
        type="button"
        data-testid="defense-add"
        className="btn btn-primary mt-group w-full"
        onClick={() => void submit()}
      >
        <Icon name="plus" />
        {t.formSubmit}
      </button>
      <p className="mt-line text-xs text-ink-mute">{t.formAnchorWhy}</p>

      {message !== '' && (
        <p data-testid="defense-form-message" role="alert" className="mt-group text-sm text-red-300">
          {message}
        </p>
      )}
      {done && (
        <p data-testid="defense-form-done" className="mt-group text-sm text-cyan">
          {t.formAdded}
        </p>
      )}
    </section>
  )
}
